import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldPathByValue,
  UseFormGetValues,
} from 'react-hook-form';
import { Column, Row } from 'further-ui/components/Layout';
import Switch from 'further-ui/components/Switch';
import { PeriodEndReportFormValues } from './schema';

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

const InnerColumn = styled(Column)({
  maxWidth: '600px',
});

const LabelContainer = styled(Row)({
  flex: 1,
});

const InputsContainer = styled(Row)({
  flex: 2,
});

const ValueContainer = styled(Column)({
  flex: 1,
});

const SwitchContainer = styled(Column)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    flex: 'unset',
  },
}));

const FieldGroupContainer = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
}));

const FieldRow = styled(Row)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(theme.spacingSizes.xxs, theme.spacingSizes.xs),
  gap: theme.spacing(theme.spacingSizes.xl),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(theme.spacingSizes.sm),
  },
}));

type ToggleableFieldConfig = {
  label: string;
  valuePath: FieldPathByValue<PeriodEndReportFormValues, string>;
  includedPath: FieldPathByValue<PeriodEndReportFormValues, boolean>;
  placeholder: string;
  readOnly: boolean;
};

const generalFields: Array<ToggleableFieldConfig> = [
  {
    label: 'Fund name',
    valuePath: 'fundOverviewDatapoints.general.fundName.value',
    includedPath: 'fundOverviewDatapoints.general.fundName.included',
    placeholder: 'Enter fund name',
    readOnly: true,
  },
  {
    label: 'Fund currency',
    valuePath: 'fundOverviewDatapoints.general.fundCurrency.value',
    includedPath: 'fundOverviewDatapoints.general.fundCurrency.included',
    placeholder: 'Enter fund currency',
    readOnly: true,
  },
  {
    label: 'Vintage year',
    valuePath: 'fundOverviewDatapoints.general.vintageYear.value',
    includedPath: 'fundOverviewDatapoints.general.vintageYear.included',
    placeholder: 'Enter vintage year',
    readOnly: true,
  },
  {
    label: 'Display close dates',
    valuePath: 'fundOverviewDatapoints.general.displayCloseDate.value',
    includedPath: 'fundOverviewDatapoints.general.displayCloseDate.included',
    placeholder: 'Enter close dates',
    readOnly: true,
  },
  {
    label: 'Total commitments',
    valuePath: 'fundOverviewDatapoints.general.totalCommitments.value',
    includedPath: 'fundOverviewDatapoints.general.totalCommitments.included',
    placeholder: 'Enter total commitments',
    readOnly: true,
  },
  {
    label: 'Display total commitments per close',
    valuePath:
      'fundOverviewDatapoints.general.displayTotalCommitmentsPerClose.value',
    includedPath:
      'fundOverviewDatapoints.general.displayTotalCommitmentsPerClose.included',
    placeholder: 'Enter commitments per close',
    readOnly: true,
  },
  {
    label: 'Fund term',
    valuePath: 'fundOverviewDatapoints.general.fundTerm.value',
    includedPath: 'fundOverviewDatapoints.general.fundTerm.included',
    placeholder: 'Enter fund term',
    readOnly: false,
  },
  {
    label: 'Legal form',
    valuePath: 'fundOverviewDatapoints.general.legalForm.value',
    includedPath: 'fundOverviewDatapoints.general.legalForm.included',
    placeholder: 'Enter legal form',
    readOnly: false,
  },
  {
    label: 'Formation jurisdiction',
    valuePath: 'fundOverviewDatapoints.general.formationJurisdiction.value',
    includedPath:
      'fundOverviewDatapoints.general.formationJurisdiction.included',
    placeholder: 'Enter jurisdiction',
    readOnly: true,
  },
  {
    label: 'Investment adviser / manager',
    valuePath: 'fundOverviewDatapoints.general.investmentAdviserManager.value',
    includedPath:
      'fundOverviewDatapoints.general.investmentAdviserManager.included',
    placeholder: 'Enter adviser / manager',
    readOnly: false,
  },
  {
    label: 'General partner',
    valuePath: 'fundOverviewDatapoints.general.generalPartner.value',
    includedPath: 'fundOverviewDatapoints.general.generalPartner.included',
    placeholder: 'Enter general partner',
    readOnly: false,
  },
  {
    label: 'Reinvestment / recycling policy',
    valuePath:
      'fundOverviewDatapoints.general.reinvestmentRecyclingPolicy.value',
    includedPath:
      'fundOverviewDatapoints.general.reinvestmentRecyclingPolicy.included',
    placeholder: 'Enter reinvestment / recycling policy',
    readOnly: false,
  },
];

const investmentFocusFields: Array<ToggleableFieldConfig> = [
  {
    label: 'Stage',
    valuePath: 'fundOverviewDatapoints.investmentFocus.stage.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.stage.included',
    placeholder: 'Enter stage',
    readOnly: false,
  },
  {
    label: 'Sector',
    valuePath: 'fundOverviewDatapoints.investmentFocus.sector.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.sector.included',
    placeholder: 'Enter sector',
    readOnly: false,
  },
  {
    label: 'Geography',
    valuePath: 'fundOverviewDatapoints.investmentFocus.geography.value',
    includedPath: 'fundOverviewDatapoints.investmentFocus.geography.included',
    placeholder: 'Enter geography',
    readOnly: false,
  },
];

type ToggleableFieldRowProps = {
  config: ToggleableFieldConfig;
  control: Control<PeriodEndReportFormValues>;
};

const ToggleableFieldRow: FC<ToggleableFieldRowProps> = ({
  config,
  control,
}) => {
  return (
    <FieldRow centered breakpoint="sm">
      <LabelContainer>
        <Typography variant="h6">{config.label}</Typography>
      </LabelContainer>
      <InputsContainer centered justify="space-between">
        <ValueContainer flex={1}>
          <Controller
            control={control}
            name={config.valuePath}
            render={({ field, fieldState }) =>
              config.readOnly ? (
                <Typography variant="body2" color="grey.900">
                  {field.value}
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  placeholder={config.placeholder}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )
            }
          />
        </ValueContainer>
        <SwitchContainer flex={1}>
          <Controller
            control={control}
            name={config.includedPath}
            render={({ field }) => (
              <Switch checked={field.value} onChange={field.onChange} />
            )}
          />
        </SwitchContainer>
      </InputsContainer>
    </FieldRow>
  );
};

type FieldGroupProps = {
  title: string;
  fields: Array<ToggleableFieldConfig>;
  control: Control<PeriodEndReportFormValues>;
};

const FieldGroup: FC<FieldGroupProps> = ({ title, fields, control }) => (
  <FieldGroupContainer spacing="sm">
    <Typography variant="caption" color="grey.500" padding="8px">
      {title}
    </Typography>
    {fields.map((config, index, array) => {
      const isNotLast = index < array.length - 1;
      return (
        <Fragment key={config.valuePath}>
          <ToggleableFieldRow config={config} control={control} />
          {isNotLast ? <Divider /> : null}
        </Fragment>
      );
    })}
  </FieldGroupContainer>
);

type ReadOnlyDatapointProps = {
  label: string;
  value: string;
};

const ReadOnlyDatapoint: FC<ReadOnlyDatapointProps> = ({ label, value }) => (
  <Row spacing="xs" centered breakpoint="sm">
    <Typography variant="h6" color="grey.600" width="300px" noWrap>
      {label}
    </Typography>
    <Typography variant="body2" color="grey.900">
      {value}
    </Typography>
  </Row>
);

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
  getValues: UseFormGetValues<PeriodEndReportFormValues>;
};

const FundOverviewDatapoints: FC<Props> = ({
  control,
  readOnly,
  getValues,
}) => {
  const renderReadOnlyDatapoints = (fields: Array<ToggleableFieldConfig>) =>
    fields.map((config) => {
      const isIncluded = getValues(config.includedPath);
      const value = getValues(config.valuePath);

      return isIncluded && value ? (
        <ReadOnlyDatapoint
          key={config.valuePath}
          label={config.label}
          value={value}
        />
      ) : null;
    });

  return (
    <OuterColumn spacing="sm">
      <InnerColumn spacing="xs">
        <Typography variant={readOnly ? 'h5' : 'h6'} color="grey.900">
          Fund overview datapoints
        </Typography>
        {readOnly ? null : (
          <Typography variant="body2">
            Select which of the following datapoints you would like to feature
            in your <em>Fund overview</em> table on the opening page of your
            investor report (after the contents page).
          </Typography>
        )}
      </InnerColumn>
      {readOnly ? (
        <>
          {renderReadOnlyDatapoints(generalFields)}
          {renderReadOnlyDatapoints(investmentFocusFields)}
        </>
      ) : (
        <>
          <FieldGroup
            title="General overview items"
            fields={generalFields}
            control={control}
          />
          <FieldGroup
            title="Investment focus"
            fields={investmentFocusFields}
            control={control}
          />
        </>
      )}
    </OuterColumn>
  );
};

export default FundOverviewDatapoints;
